import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import userService from '../../services/userService';
import walletService from '../../services/walletService';
import Button from '../../components/common/Button';
import { useToast } from '../../hooks/useToast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faWallet } from '@fortawesome/free-solid-svg-icons';

export default function SuperAdminDashboard() {
  const { showToast } = useToast();
  const [stats, setStats] = useState(null);
  const [limiterStatus, setLimiterStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [statsRes, limiterRes, balRes] = await Promise.allSettled([
          userService.getSuperadminDashboardStats(),
          userService.getLimiterStatus(),
          walletService.getBalance(),
        ]);

        if (!active) return;

        if (statsRes.status === 'fulfilled') {
          setStats(statsRes.value?.data?.data || null);
        } else {
          setStats(null);
        }

        if (limiterRes.status === 'fulfilled') {
          setLimiterStatus(limiterRes.value?.data?.data || null);
        } else {
          setLimiterStatus(null);
        }

        if (balRes.status === 'fulfilled') {
          setBalance(balRes.value?.data?.data || null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const handleClearCache = async () => {
    if (!window.confirm('Clear all Redis cache? This may temporarily slow requests.')) return;
    setClearing(true);
    try {
      await userService.clearCache();
      showToast('Cache cleared successfully', 'success');
    } catch {
      showToast('Failed to clear cache', 'error');
    } finally {
      setClearing(false);
    }
  };

  const userTotal = Object.values(stats?.users_by_role || {}).reduce((sum, n) => sum + (n || 0), 0);
  const artTotal = Object.values(stats?.arts_by_status || {}).reduce((sum, n) => sum + (n || 0), 0);

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px' }}>
        {loading ? <p>Loading stats...</p> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <div className="application-card">
              <h4 style={{ marginTop: 0 }}>Total Users</h4>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{userTotal}</div>
            </div>
            <div className="application-card">
              <h4 style={{ marginTop: 0 }}>Total Artworks</h4>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{artTotal}</div>
            </div>
            <div className="application-card">
              <h4 style={{ marginTop: 0 }}>Pending Tokenization</h4>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{stats?.pending_tokenization_applications || 0}</div>
            </div>
            <div className="application-card">
              <h4 style={{ marginTop: 0 }}>Active Auctions</h4>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{stats?.active_auctions || 0}</div>
            </div>
            <div className="application-card">
              <h4 style={{ marginTop: 0 }}>Limiter Backend</h4>
              <div style={{ fontSize: 24, fontWeight: 700, textTransform: 'uppercase' }}>
                {limiterStatus?.mode || 'unknown'}
              </div>
              <div style={{ marginTop: 8, color: '#6b7280', fontSize: 12, wordBreak: 'break-all' }}>
                {limiterStatus?.storage_uri || 'storage uri unavailable'}
              </div>
            </div>

            {balance && (
              <div className="application-card" style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', color: '#fff' }}>
                <h4 style={{ marginTop: 0, color: '#94a3b8' }}>
                  <FontAwesomeIcon icon={faWallet} style={{ marginRight: 6 }} />Platform Wallet
                </h4>
                <div style={{ fontSize: 28, fontWeight: 700 }}>
                  ${Number(balance.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          <Button variant="danger" onClick={handleClearCache} disabled={clearing}>
            <FontAwesomeIcon icon={faTrashCan} style={{ marginRight: 6 }} />
            {clearing ? 'Clearing...' : 'Clear Cache'}
          </Button>
          <Button variant="primary" to="/superadmin/dashboard/wallet">
            <FontAwesomeIcon icon={faWallet} style={{ marginRight: 6 }} />Manage Wallet
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
